import { useCallback, useEffect, useMemo } from 'react';

import { ProfilesResponse, useProfilesData } from '@/features/user';

import { analyticsConfig } from '../config';
import { analytics } from '../index';
import type { ExceptionParams, UserProperties } from '../service';
import type TrackEvent from '../types';

/**
 * Session storage key for analytics session ID
 */
const ANALYTICS_SESSION_ID_KEY = 'analytics_session_id';

/**
 * Remove null and undefined values from an object
 * This keeps the event payload clean and reduces data size
 */
const removeNullishValues = <T extends Record<string, unknown>>(
  obj: T
): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== null && value !== undefined
    )
  ) as Partial<T>;
};

/**
 * Get comprehensive event metadata
 * Includes app info, browser, device, session, and timing information
 */
const getEventMetadata = () => {
  const now = new Date();

  return {
    // App information
    app_name: analyticsConfig.app.name,
    app_version: analyticsConfig.app.version,
    app_environment: analyticsConfig.app.environment,

    // Timing information
    event_timestamp: now.toISOString(),
    event_timestamp_unix: now.getTime(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezone_offset: now.getTimezoneOffset(),

    // Browser information
    user_agent: navigator.userAgent,
    browser_language: navigator.language,
    browser_languages: navigator.languages.join(','),
    browser_online: navigator.onLine,

    // Screen information
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    screen_color_depth: window.screen.colorDepth,
    screen_pixel_ratio: window.devicePixelRatio,

    // Viewport information
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,

    // Device information
    device_memory:
      'deviceMemory' in navigator
        ? ((navigator as { deviceMemory?: number }).deviceMemory ?? null)
        : null,
    hardware_concurrency: navigator.hardwareConcurrency ?? null,

    // Page information
    page_url: globalThis.location.href,
    page_path: globalThis.location.pathname,
    page_referrer: document.referrer || null,
    page_title: document.title,

    // Session information
    session_id: sessionStorage.getItem(ANALYTICS_SESSION_ID_KEY) ?? null,
  };
};

/**
 * Initialize session ID if not exists
 */
const initializeSession = () => {
  // eslint-disable-next-line sonarjs/different-types-comparison
  if (globalThis.window === undefined) return;

  if (!sessionStorage.getItem(ANALYTICS_SESSION_ID_KEY)) {
    // Generate cryptographically secure random session ID
    const randomPart = globalThis.crypto
      ? crypto.getRandomValues(new Uint32Array(1))[0]?.toString(36)
      : // eslint-disable-next-line sonarjs/pseudo-random
        Math.random().toString(36).slice(2, 9);
    const sessionId = `${Date.now()}-${randomPart}`;
    sessionStorage.setItem(ANALYTICS_SESSION_ID_KEY, sessionId);
  }
};

export const getUserAttributesForAnalytics = (
  selectedProfile?: ProfilesResponse.Profile,
  selectedCourse?: ProfilesResponse.EnrolledCourse
): UserProperties => {
  const userAttributes: UserProperties = {};
  if (selectedProfile) {
    userAttributes.user_id = selectedProfile.id;
    userAttributes.user_role = selectedProfile.role;
    userAttributes.user_grade = selectedProfile.grade ?? null;
    userAttributes.user_country = selectedProfile.country ?? null;
    userAttributes.user_state = selectedProfile.state ?? null;
    userAttributes.user_city = selectedProfile.city ?? null;
  }
  if (selectedCourse) {
    userAttributes.course_name = selectedCourse.course_name ?? null;
    userAttributes.course_category = selectedCourse.category ?? null;
    userAttributes.course_status = selectedCourse.status ?? null;
  }
  return removeNullishValues(userAttributes) as UserProperties;
};

/**
 * Enhanced analytics hook with automatic metadata enrichment
 * Automatically includes user data, device info, timing, and session context
 *
 * @example
 * ```tsx
 * // Track events - all metadata automatically included
 * trackEvent('buttonClicked', 1, { button_name: 'submit' });
 * ```
 */
export const useAnalytics = () => {
  const { selectedProfile, selectedCourse } = useProfilesData();

  // Initialize session on mount
  useEffect(() => {
    initializeSession();
  }, []);

  const getUserAttributes = useCallback((): UserProperties => {
    return getUserAttributesForAnalytics(selectedProfile, selectedCourse);
  }, [selectedProfile, selectedCourse]);

  // Memoize analytics methods to prevent unnecessary re-renders
  const analyticsApi = useMemo(
    () => ({
      /**
       * Track a custom event with automatic metadata enrichment
       * Includes user properties, device info, timing, and session data
       * Automatically filters out null and undefined values
       * @param name - Event name
       * @param version - Event version
       * @param attributes - Event attributes
       */
      trackEvent: <T extends keyof TrackEvent>(
        name: T,
        version: keyof TrackEvent[T],
        attributes: TrackEvent[T][typeof version]
      ) => {
        const metadata = getEventMetadata();
        const userProperties = getUserAttributes();
        const enrichedAttributes = removeNullishValues({
          ...(attributes as Record<string, unknown>),
          ...metadata,
          ...userProperties,
        });

        analytics.trackEvent(
          name,
          version,
          enrichedAttributes as TrackEvent[T][typeof version]
        );
      },

      /**
       * Track an exception/error with automatic metadata enrichment
       * Includes device info, timing, and session data
       * Automatically filters out null and undefined values
       * @param params - Exception parameters
       */
      trackException: (params: ExceptionParams) => {
        const metadata = getEventMetadata();
        const userProperties = getUserAttributes();
        const enrichedParams = removeNullishValues({
          ...params,
          ...metadata,
          ...userProperties,
        });
        analytics.trackException(enrichedParams);
      },

      /**
       * Track a page view with automatic metadata enrichment
       * Includes device info, timing, and session data
       * Automatically filters out null and undefined values
       * @param pagePath - Page path
       * @param pageTitle - Optional page title
       */
      trackPageView: (pagePath: string, pageTitle?: string) => {
        const metadata = getEventMetadata();
        const userProperties = getUserAttributes();
        const cleanMetadata = removeNullishValues(metadata);
        analytics.trackPageView(pagePath, pageTitle, {
          ...cleanMetadata,
          ...userProperties,
        });
      },

      /**
       * Get current user ID
       */
      getUserId: () => {
        return analytics.getUserId();
      },

      /**
       * Get current user properties
       */
      getUserProperties: () => {
        return analytics.getUserProperties();
      },

      /**
       * Clear user ID and session (useful for logout)
       */
      clearUserId: () => {
        analytics.clearUserId();
        sessionStorage.removeItem(ANALYTICS_SESSION_ID_KEY);
      },

      /**
       * Set additional user properties
       * @param properties - User properties to set
       */
      setUserProperties: (properties: UserProperties) => {
        analytics.setUserProperties(properties);
      },

      /**
       * Get current session ID
       */
      getSessionId: () => {
        return sessionStorage.getItem(ANALYTICS_SESSION_ID_KEY);
      },
    }),
    [getUserAttributes]
  );

  return analyticsApi;
};
